import type { ActionDescription } from "@smartsuite/automation-engine-api";
import { Availability } from "@smartsuite/automation-engine-api";
import {
  Badge,
  DropdownPanel,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Scrollbar,
  SubmenuMenuOption,
  Text,
  Tooltip,
  TooltipButton,
} from "@smartsuite/react-ui/lib";
import { GrayColors } from "@smartsuite/types";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import type React from "react";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import * as R from "remeda";
import {
  capitalize,
  translateAction,
  translateIfPresent,
} from "../../locales/i18n";
import {
  intercomOpenAutomationActionsArticle,
  intercomOpenIntegrationsArticle,
} from "../../services/intercom.service";
import { useRootStore } from "../../stores/hooks";
import {
  ActionLabel,
  integrationsIntercomIds,
  intergrationsMainIntercomId,
  isAiIntegration,
  isSmartSuiteIntegration,
} from "../../utils/constants";
import { getLocalCssColorVariables } from "../../utils/cssVariablesUtils";
import * as StyledUtils from "../../utils/dropdown";
import { getIntegrationLogo } from "../../utils/integration-logo";
import { capitalizeFirstLetter } from "../../utils/strings";
import { DropdownOnboardingHelp } from "../DropdownOnboardingHelp";
import { InformationTooltip } from "../InformationTooltip";
import * as Style from "./styles";
import { BetaWrapper, betaStyle } from "./styles";
const triggersForMergeAction = [
  "created-record",
  "updated-record",
  "form-submitted",
  "matches-condition",
];

export const ActionMenu = observer<{
  menuButtonRef: React.MutableRefObject<HTMLElement | null>;
  isEnabledWidthCalculation?: boolean;
  isMenuOpen: boolean;
  setMenuOpen: (isOpen: boolean) => void;
  onActionChange: (integrationId: string, actionId: string) => void;
  setActiveAction?: (actionHash?: string) => void;
  setActiveTrigger?: (isActive?: boolean) => void;
  setActiveSection?: (
    item: {
      type: "trigger" | "action" | "looping";
      hash: string;
    } | null,
  ) => void;
  filterActions?: string[];
  showOnboarding?: boolean;
  dropdownMaxWidth?: number;
  dropdownMinWidth?: number;
  showAdvancedLogic?: boolean;
  /** Nested Advanced Logic (Етап 7): per-item visibility within the
   * advanced-logic section, gated by the caller (root/branch depth rules).
   * Omitted item defaults to visible -- historic behavior for consumers that
   * don't need per-item control. */
  advancedLogicItems?: { looping?: boolean; branches?: boolean };
}>(
  ({
    isEnabledWidthCalculation = true,
    menuButtonRef,
    isMenuOpen,
    setMenuOpen,
    onActionChange,
    setActiveSection,
    setActiveAction,
    setActiveTrigger,
    showOnboarding,
    dropdownMaxWidth,
    dropdownMinWidth,
    showAdvancedLogic = true,
    advancedLogicItems,
    filterActions,
  }) => {
    const store = useRootStore();
    const integrations = store.integrations.list.filter(
      (integration) =>
        integration.info?.availability !== Availability.AVAILABILITY_DISABLED &&
        integration.info?.availability !== Availability.AVAILABILITY_HIDDEN,
    );
    const [enableWidthCalculation, setEnableWidthCalculation] =
      useState<boolean>(isEnabledWidthCalculation);

    const dropdownPanelRef = useRef<HTMLDivElement>(null);

    const ssIntegrations = integrations
      .filter((integration) =>
        isSmartSuiteIntegration(integration.integrationId),
      )
      .sort((a, b) => (a.actionsRank ?? 1000) - (b.actionsRank ?? 1000));

    const aiIntegrations = integrations
      .filter((integration) => isAiIntegration(integration.integrationId))
      .sort((a, b) => (a.actionsRank ?? 1000) - (b.actionsRank ?? 1000));
    const integrationWithActions = integrations.filter((integration) => {
      return (
        !isSmartSuiteIntegration(integration.integrationId) &&
        !isAiIntegration(integration.integrationId) &&
        integration.actions.filter(isActionAvailable).length > 0
      );
    });

    // Merge SS action should be shown only for create, update, form submitted, match conditions triggers
    const getSsActions = (): ActionDescription[] => {
      const allActions = ssIntegrations
        .flatMap((integration) => integration.actions)
        .filter(isActionAvailable);

      const isMergeActionAllowed = triggersForMergeAction.includes(
        store.automations.candidate.self.trigger.triggerReference?.triggerId ??
          "",
      );

      if (!isMergeActionAllowed) {
        return (
          allActions.filter((action) => action.actionId !== "merge-records") ??
          []
        );
      }

      return filterActions
        ? (allActions.filter(
            (action) => !filterActions?.includes(action.actionId),
          ) ?? [])
        : allActions;
    };

    const getAiActions = (): Array<{
      action: ActionDescription;
      integrationId: string;
    }> => {
      const allActions = aiIntegrations.flatMap((integration) =>
        integration.actions.filter(isActionAvailable).map((action) => ({
          action,
          integrationId: integration.integrationId,
        })),
      );

      return filterActions
        ? allActions.filter(
            ({ action }) => !filterActions.includes(action.actionId),
          )
        : allActions;
    };

    const getSSActionLogo = (label: string): object => {
      const triggerLabel = label.toLowerCase();
      switch (true) {
        case triggerLabel.includes(ActionLabel.createRecord):
          return {
            iconName: "plus-circle",
          };
        case triggerLabel.includes(ActionLabel.updateRecord):
          return {
            iconName: "edit-smaller",
          };
        case triggerLabel.includes(ActionLabel.deleteRecord):
          return {
            iconName: "trash",
          };
        case triggerLabel.includes(ActionLabel.sendEmail):
        case triggerLabel.includes(ActionLabel.sendEmailFromSmartsuite):
          return {
            iconName: "email",
            iconSize: "S",
          };
        case triggerLabel.includes(ActionLabel.findRecords):
          return {
            iconName: "search",
          };
        case triggerLabel.includes(ActionLabel.mergeRecords):
          return {
            iconName: "merge",
            iconSize: "S",
          };
        case triggerLabel.includes(ActionLabel.generatePdf):
          return {
            iconName: "pdf",
          };
        case triggerLabel.includes(ActionLabel.commentRecord):
          return {
            iconName: "comments",
          };
        case triggerLabel.includes(ActionLabel.sendWebhook):
          return {
            iconName: "webhook",
          };
        case triggerLabel.includes(ActionLabel.sendNotification):
          return {
            iconName: "bell",
          };
        default:
          return {
            iconName: "edit-smaller",
          };
      }
    };

    const onClickRequest = useCallback(() => {
      window.open("https://feedback.smartsuite.com/");
    }, []);

    // One open = one selection. Closing the menu does not remove it right
    // away: DropdownPanel keeps the panel mounted (and clickable) for its
    // 250ms CSS exit transition, and consumers defer the add by another 250ms,
    // so a double-click used to land twice and add two blocks (STR-14493).
    // Every option goes through selectAction, so the guard covers the advanced
    // logic items and the integration actions alike.
    const isSelectedRef = useRef(false);
    useEffect(() => {
      if (isMenuOpen) {
        isSelectedRef.current = false;
      }
    }, [isMenuOpen]);

    const selectAction = (
      integrationId: string,
      actionId: string,
      onBeforeChange?: () => void,
    ): void => {
      if (isSelectedRef.current) {
        return;
      }
      isSelectedRef.current = true;
      onBeforeChange?.();
      setMenuOpen(false);
      onActionChange(integrationId, actionId);
    };

    const onLoopingClick = (): void => {
      selectAction("looping", "loop", () => {
        setActiveAction?.(undefined);
        setActiveTrigger?.(undefined);
        setActiveSection?.({ type: "looping", hash: "" });
      });
    };

    const onBranchesClick = (): void => {
      selectAction("branches", "branches", () => {
        setActiveAction?.(undefined);
        setActiveTrigger?.(undefined);
      });
    };

    const showLooping =
      showAdvancedLogic && (advancedLogicItems?.looping ?? true);
    // per-item gate is the CALLER's job now (root: AddActionButton's
    // isShowAdvancedLogic already hides the section once a root loop or any
    // branches exist; branch pickers pass explicit per-item visibility).
    // The `conditional_logic` UCES flag gates the item globally -- this menu
    // is the single render point for every add-branches entry.
    const showBranches =
      store.featureFlags.conditionalLogic &&
      showAdvancedLogic &&
      (advancedLogicItems?.branches ?? true);

    return (
      <DropdownPanel
        dropdownMaxWidth={dropdownMaxWidth}
        minWidth={dropdownMinWidth}
        className="dropdown-panel"
        clickOutsideExcept={[".menu-option-wrapper"]}
        dropdownWidth={350}
        enableWidthCalculation={enableWidthCalculation}
        isActive={isMenuOpen}
        zIndex={1001}
        targetElementRef={
          menuButtonRef as unknown as MutableRefObject<HTMLElement>
        }
        portalStyles={getLocalCssColorVariables(store.theme)}
        onClickOutside={() => {
          setMenuOpen(false);
        }}
        my="left top+2"
      >
        {R.isTruthy(showOnboarding) && (
          <DropdownOnboardingHelp
            helpText={t("dropdown-help-onboarding.action-help-text")}
            variableName="hide-action-help-onboarding"
            onButtonClick={() => {
              if (isEnabledWidthCalculation) {
                setEnableWidthCalculation(false);
                setTimeout(() => {
                  setEnableWidthCalculation(true);
                }, 0);
              }
            }}
          />
        )}

        <Scrollbar
          style={{ height: "100vh", maxHeight: 507 }}
          noScrollX
          gutterTop={2}
          gutterBottom={2}
          gutterRight={2}
        >
          <StyledUtils.MenuWrapper ref={dropdownPanelRef}>
            <MenuOptionWrapper>
              {(showLooping || showBranches) && (
                <>
                  <StyledUtils.IntegrationTitle>
                    <InformationTooltip
                      tooltipText={t("general.advanced-logic-tooltip-text")}
                      onClick={() => {
                        intercomOpenAutomationActionsArticle();
                      }}
                    >
                      {capitalizeFirstLetter(t("general.advanced-logic-title"))}
                    </InformationTooltip>
                  </StyledUtils.IntegrationTitle>
                  {showLooping && (
                    <Style.MenuOptionList onClick={onLoopingClick}>
                      <MenuOption
                        iconName="refresh"
                        iconSize="M"
                        renderRightContent={
                          <Tooltip
                            className="trigger-help-tooltip"
                            placements={["right", "left"]}
                            padding={2}
                            target={
                              <TooltipButton
                                iconName="info-filled"
                                iconSize="S"
                                colors={{
                                  default: GrayColors["G-4"],
                                  hover: GrayColors["G-8"],
                                }}
                                handleClick={(event) => {
                                  event.stopPropagation();
                                  intercomOpenAutomationActionsArticle();
                                }}
                              />
                            }
                            renderTooltipContent={
                              <Text size="xs" weight="semibold" color="reverse">
                                {t("general.action-looping-tooltip-text")}
                              </Text>
                            }
                          />
                        }
                      >
                        {t("general.looping")}
                      </MenuOption>
                    </Style.MenuOptionList>
                  )}

                  {showBranches && (
                    <Style.MenuOptionList onClick={onBranchesClick}>
                      <MenuOption
                        iconName="alt-route"
                        // alt-route ships only in the icon pack's MISC bucket;
                        // "M" would render blank. Match Looping's 16px glyph.
                        iconSize="MISC"
                        iconDimension={{ width: 16, height: 16 }}
                        renderRightContent={
                          <Tooltip
                            className="trigger-help-tooltip"
                            placements={["right", "left"]}
                            padding={2}
                            target={
                              <TooltipButton
                                iconName="info-filled"
                                iconSize="S"
                                colors={{
                                  default: GrayColors["G-4"],
                                  hover: GrayColors["G-8"],
                                }}
                                handleClick={(event) => {
                                  event.stopPropagation();
                                  intercomOpenAutomationActionsArticle();
                                }}
                              />
                            }
                            renderTooltipContent={
                              <Text size="xs" weight="semibold" color="reverse">
                                {t(
                                  "general.action-conditional-logic-tooltip-text",
                                )}
                              </Text>
                            }
                          />
                        }
                      >
                        {t("general.conditional-logic")}
                      </MenuOption>
                    </Style.MenuOptionList>
                  )}
                </>
              )}

              {/* SS integration action list */}
              {ssIntegrations.length > 0 && (
                <>
                  <StyledUtils.IntegrationTitle
                    pt={showLooping || showBranches ? 16 : 0}
                  >
                    <InformationTooltip
                      tooltipText={t("general.action-smartsuite-tooltip-text")}
                      onClick={() => {
                        intercomOpenAutomationActionsArticle();
                      }}
                    >
                      {`SmartSuite ${capitalizeFirstLetter(
                        t("general.action.other"),
                      )}`}
                    </InformationTooltip>
                  </StyledUtils.IntegrationTitle>

                  <Style.MenuOptionList>
                    {getSsActions().map((action) => {
                      return (
                        <MenuOption
                          className="with-info-tooltip"
                          key={`action-${action.actionId}`}
                          {...getSSActionLogo(action.actionId)}
                          onClick={(e) => {
                            if (
                              action.info?.availability ===
                              Availability.COMMING_SOON
                            ) {
                              return;
                            }
                            const actionIntegration = ssIntegrations.find(
                              (integration) =>
                                integration.actions.find(
                                  (a) => a.actionId === action.actionId,
                                ),
                            );
                            selectAction(
                              actionIntegration?.integrationId ?? "",
                              action.actionId,
                            );
                            e.stopPropagation();
                          }}
                          renderRightContent={
                            action.info?.availability !==
                              Availability.COMMING_SOON && (
                              <Tooltip
                                className="trigger-help-tooltip"
                                placements={["right", "left"]}
                                padding={2}
                                target={
                                  <TooltipButton
                                    iconName="info-filled"
                                    iconSize="S"
                                    colors={{
                                      default: GrayColors["G-4"],
                                      hover: GrayColors["G-8"],
                                    }}
                                    handleClick={(event) => {
                                      event.stopPropagation();
                                      intercomOpenAutomationActionsArticle();
                                    }}
                                  />
                                }
                                renderTooltipContent={
                                  <Text
                                    size="xs"
                                    weight="semibold"
                                    color="reverse"
                                  >
                                    {action.info?.description}
                                  </Text>
                                }
                              />
                            )
                          }
                          // TODO: Respove issues with popups
                          // popoverContainerClassName={
                          //   "with-info-tooltip-popover"
                          // }
                        >
                          {translateAction(action)}
                          {action.info?.availability === Availability.BETA && (
                            <BetaWrapper>
                              <Badge styles={betaStyle}>BETA</Badge>
                            </BetaWrapper>
                          )}
                          {action.info?.availability ===
                            Availability.COMMING_SOON && (
                            <BetaWrapper>
                              <Badge styles={Style.commingSoonStyle}>
                                COMING SOON
                              </Badge>
                            </BetaWrapper>
                          )}
                        </MenuOption>
                      );
                    })}
                  </Style.MenuOptionList>
                </>
              )}
              {/* AI integrations action list */}
              {aiIntegrations.length > 0 && (
                <>
                  <StyledUtils.IntegrationTitle pt={16}>
                    <InformationTooltip
                      tooltipText={t("general.action-other-tooltip-text")}
                      onClick={() => {
                        intercomOpenIntegrationsArticle(
                          intergrationsMainIntercomId,
                        );
                      }}
                    >
                      {t("actions.AI Platform")}
                    </InformationTooltip>
                  </StyledUtils.IntegrationTitle>

                  <Style.MenuOptionList>
                    {getAiActions().map(({ action, integrationId }) => {
                      const IconIntegration = getIntegrationLogo(integrationId);

                      return (
                        <MenuOption
                          className="with-info-tooltip"
                          key={`action-${integrationId}-${action.actionId}`}
                          renderIcon={() => (
                            <IconIntegration style={{ marginRight: "10px" }} />
                          )}
                          onClick={() => {
                            if (
                              action.info?.availability ===
                              Availability.COMMING_SOON
                            ) {
                              return;
                            }
                            selectAction(integrationId, action.actionId);
                          }}
                          infoData={{
                            onIconClick: (event) => {
                              event.stopPropagation();
                              intercomOpenIntegrationsArticle(
                                integrationsIntercomIds[action.actionId] ||
                                  intergrationsMainIntercomId,
                              );
                            },
                          }}
                        >
                          {translateAction(action)}
                          {(action.info?.beta === true ||
                            action.info?.availability ===
                              Availability.BETA) && (
                            <BetaWrapper>
                              <Badge styles={betaStyle}>BETA</Badge>
                            </BetaWrapper>
                          )}
                          {action.info?.availability ===
                            Availability.COMMING_SOON && (
                            <BetaWrapper>
                              <Badge styles={Style.commingSoonStyle}>
                                COMING SOON
                              </Badge>
                            </BetaWrapper>
                          )}
                        </MenuOption>
                      );
                    })}
                  </Style.MenuOptionList>
                </>
              )}

              {/* Integrations list */}
              {integrationWithActions.length > 0 ? (
                <StyledUtils.IntegrationTitle pt={16}>
                  <InformationTooltip
                    tooltipText={t("general.action-other-tooltip-text")}
                    onClick={() => {
                      intercomOpenIntegrationsArticle(
                        intergrationsMainIntercomId,
                      );
                    }}
                  >
                    {capitalize(t("general.integration.other"))}
                  </InformationTooltip>
                </StyledUtils.IntegrationTitle>
              ) : null}

              <Style.MenuOptionList>
                {integrationWithActions
                  .sort(
                    (a, b) => (a.actionsRank ?? 1000) - (b.actionsRank ?? 1000),
                  )
                  .map((integration) => {
                    const IconIntegration = getIntegrationLogo(
                      integration.integrationId,
                    );
                    if (
                      integration.actions.length === 0 &&
                      integration.triggers.length === 0
                    ) {
                      return (
                        <MenuOption
                          className="with-info-tooltip"
                          key={integration.integrationId}
                          renderIcon={() => {
                            return (
                              <IconIntegration
                                style={{ marginRight: "10px" }}
                              />
                            );
                          }}
                        >
                          {translateIfPresent(
                            `caption.${integration.label}`,
                            integration.label,
                          )}
                          <BetaWrapper>
                            <Badge styles={Style.commingSoonStyle}>
                              COMING SOON
                            </Badge>
                          </BetaWrapper>
                        </MenuOption>
                      );
                    }
                    return (
                      <SubmenuMenuOption
                        key={integration.integrationId}
                        name={integration.label}
                        my="left top"
                        at="right top-4"
                        parentMenu={dropdownPanelRef}
                        renderElementAfterName={
                          integration.actions.length === 0 &&
                          integration.triggers.length === 0 ? (
                            <BetaWrapper>
                              <Badge styles={Style.commingSoonStyle}>
                                COMING SOON
                              </Badge>
                            </BetaWrapper>
                          ) : integration.info?.availability ===
                            Availability.BETA ? (
                            <BetaWrapper>
                              <Badge styles={betaStyle}>BETA</Badge>
                            </BetaWrapper>
                          ) : (
                            <></>
                          )
                        }
                        renderIcon={() => {
                          return (
                            <IconIntegration style={{ marginRight: "10px" }} />
                          );
                        }}
                        autohide
                        delayShow
                        zIndex={1000}
                      >
                        <MenuOptionWrapper maxHeight={507} smallListPaddings>
                          <Style.MenuOptionList>
                            {integration.actions
                              .filter(isActionAvailable)
                              .map((action) => (
                                <Style.MenuOptionWrapper key={action.actionId}>
                                  <MenuOption
                                    key={action.actionId}
                                    renderIcon={() => {
                                      return (
                                        <IconIntegration
                                          style={{ marginRight: "10px" }}
                                        />
                                      );
                                    }}
                                    onClick={() => {
                                      if (
                                        action.info?.availability ===
                                        Availability.COMMING_SOON
                                      ) {
                                        return;
                                      }
                                      selectAction(
                                        integration.integrationId,
                                        action.actionId,
                                      );
                                    }}
                                    className="submenu-item"
                                    infoData={{
                                      onIconClick: (event) => {
                                        event.stopPropagation();
                                        intercomOpenIntegrationsArticle(
                                          integrationsIntercomIds[
                                            action.actionId
                                          ] || intergrationsMainIntercomId,
                                        );
                                      },
                                    }}
                                  >
                                    {translateAction(action)}
                                    {(action.info?.beta === true ||
                                      action.info?.availability ===
                                        Availability.BETA) && (
                                      <BetaWrapper>
                                        <Badge styles={betaStyle}>BETA</Badge>
                                      </BetaWrapper>
                                    )}
                                    {action.info?.availability ===
                                      Availability.COMMING_SOON && (
                                      <BetaWrapper>
                                        <Badge styles={Style.commingSoonStyle}>
                                          COMING SOON
                                        </Badge>
                                      </BetaWrapper>
                                    )}
                                  </MenuOption>
                                </Style.MenuOptionWrapper>
                              ))}
                          </Style.MenuOptionList>
                        </MenuOptionWrapper>
                      </SubmenuMenuOption>
                    );
                  })}
              </Style.MenuOptionList>

              <MenuLineSeparator />

              <Style.StyledTriggerRequest>
                {t("automation-form.dont-see-action-you-need")}{" "}
                <button type="button" onClick={onClickRequest}>
                  {t("automation-form.request")}
                </button>
              </Style.StyledTriggerRequest>
            </MenuOptionWrapper>
          </StyledUtils.MenuWrapper>
        </Scrollbar>
      </DropdownPanel>
    );
  },
);

// Filter helper to check if action is available (not disabled or hidden)
const isActionAvailable = (action: ActionDescription): boolean => {
  return (
    action.info?.availability !== Availability.AVAILABILITY_DISABLED &&
    action.info?.availability !== Availability.AVAILABILITY_HIDDEN
  );
};
